import React from 'react';
import { CopyOutlined, LoginOutlined, TruckOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { apiRequests } from "src/shared/api/api";

const linksKeys = [
  { key: "general", title: "Основная" },
  { key: "courier", title: "Курьер" },
  { key: "picker", title: "Сборщик" },
];

const DocSalesOrderDrawer = ({ deliveryInfo, docId, onSave, isNew = false }) => {
  const [visible, setVisible] = useState(false);
  const [isGetLinks, setIsGetLinks] = useState(false);
  const [linksLoading, setLinksLoading] = useState(false);
  const [links, setLinks] = useState({});
  const [form] = Form.useForm();
  // eslint-disable-next-line no-unused-vars
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [, setDeliveryPrice] = useState(deliveryInfo?.delivery_price || null);

  const getDocSalesLinks = useCallback(() => {
    if (!docId) return;
    setLinksLoading(true);
    apiRequests.docs
      .getSalesLinks(docId)
      .then((res) => {
        const data = res.data || {};
        const normalizedLinks = Array.isArray(data)
          ? data.reduce((acc, item, index) => {
            acc[index] = item;
            return acc;
          }, {})
          : data;

        setLinks(normalizedLinks);
        setIsGetLinks(true);
      })
      .catch((err) => {
        console.error("Ошибка загрузки ссылок:", err);
        message.error("Не удалось получить ссылки");
      })
      .finally(() => setLinksLoading(false));
  }, [docId]);

  const updateDeliveryInfo = useCallback(async (values) => {
    if (!docId) return;

    setLoadingDelivery(true);
    try {
      const response = await apiRequests.docs.postSalesDeliveryInfo(docId, values)
      const updatedInfo = response.data;

      setDeliveryPrice(updatedInfo.delivery_price);

      message.success("Информация о доставке обновлена");
      return updatedInfo;
    } catch (err) {
      console.error("Ошибка обновления информации о доставке:", err);
      message.error("Не удалось обновить информацию о доставке");
      throw err;
    } finally {
      setLoadingDelivery(false);
    }
  }, [docId]);

  useEffect(() => {
    if (visible && deliveryInfo && form) {
      form.resetFields();

      const recipientFullName = deliveryInfo.recipient
        ? `${deliveryInfo.recipient.name || ""} ${deliveryInfo.recipient.surname || ""}`.trim()
        : "";

      form.setFieldsValue({
        ...deliveryInfo,
        delivery_date: deliveryInfo.delivery_date
          ? dayjs(deliveryInfo.delivery_date * 1000)
          : null,
        recipient_fullname: recipientFullName,
        recipient_phone: deliveryInfo.recipient?.phone || "",
        delivery_price: deliveryInfo.delivery_price || undefined,
      });

      setDeliveryPrice(deliveryInfo.delivery_price || null);
    }
  }, [visible, deliveryInfo, form]);

  const ensureAbsoluteUrl = (url) => {
    if (!url) return "#";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const copyFieldValue = (fieldName) => {
    const value = form.getFieldValue(fieldName);
    if (!value) {
      message.warning("Нет данных для копирования");
      return;
    }

    navigator.clipboard
      .writeText(value)
      .then(() => message.success("Скопировано в буфер обмена"))
      .catch((err) => {
        console.error("Ошибка копирования:", err);
        message.error("Не удалось скопировать");
      });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const deliveryDate = values.delivery_date
        ? Math.floor(values.delivery_date.valueOf() / 1000)
        : null;

      const fullName = values.recipient_fullname || "";
      const nameParts = fullName.trim().split(/\s+/);
      const name = nameParts[0] || "";
      const surname = nameParts.slice(1).join(" ") || "";

      const payload = {
        ...values,
        delivery_date: deliveryDate,
        recipient: { name, surname, phone: values.recipient_phone },
      };

      delete payload.recipient_fullname;
      delete payload.recipient_phone;

      await updateDeliveryInfo(payload);

      if (onSave) {
        onSave(payload);
      }

      setVisible(false);
    } catch (err) {
      console.error("Ошибка при сохранении:", err);
      if (err.errorFields && err.errorFields.length > 0) {
        message.error("Пожалуйста, проверьте введенные данные");
      } else {
        message.error("Ошибка при сохранении данных");
      }
    }
  };

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <>
      <Button
        onClick={() => setVisible(true)}
        style={{ width: "100%", marginBottom: 12 }}
        icon={<TruckOutlined />}
      >
        Доставка
      </Button>

      <Drawer
        open={visible}
        onClose={handleClose}
        title="Данные о доставке"
        placement="right"
        width={500}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose}>Отмена</Button>
            <Button type="primary" onClick={handleSave} loading={loadingDelivery}>
              Сохранить
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Адрес">
              <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                <Form.Item name="address" style={{ flex: 1, marginBottom: 0 }}>
                  <Input variant="borderless" />
                </Form.Item>
                <Button icon={<CopyOutlined />} onClick={() => copyFieldValue("address")} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Стоимость доставки">
              <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                <Form.Item
                  name="delivery_price"
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{
                    type: 'number',
                    min: 0,
                    message: 'Стоимость должна быть положительным числом'
                  }]}
                >
                  <InputNumber
                    placeholder="Введите стоимость"
                    style={{ width: '100%' }}
                    min={0}
                    step={1}
                    precision={2}
                    formatter={(value) => {
                      if (!value && value !== 0) return '';
                      return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      return value.replace(/\s/g, '').replace(',', '.');
                    }}
                    variant="borderless"
                    onKeyPress={(e) => {
                      // Разрешаем только цифры, точку, Backspace, Delete, Tab, Arrow keys
                      if (!/[0-9.]/.test(e.key) &&
                        e.key !== 'Backspace' &&
                        e.key !== 'Delete' &&
                        e.key !== 'Tab' &&
                        e.key !== 'ArrowLeft' &&
                        e.key !== 'ArrowRight' &&
                        e.key !== 'ArrowUp' &&
                        e.key !== 'ArrowDown') {
                        e.preventDefault();
                      }
                      // Запрещаем ввод точки, если она уже есть
                      if (e.key === '.' && e.target.value.includes('.')) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <span>₽</span>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Дата доставки">
              <Form.Item name="delivery_date" style={{ flex: 1, marginBottom: 0 }}>
                <DatePicker
                  showTime
                  format="DD.MM.YYYY HH:mm"
                  style={{ width: "100%" }}
                  placeholder="Выберите дату и время"
                  variant="borderless"
                />
              </Form.Item>
            </Descriptions.Item>

            <Descriptions.Item label="Примечание">
              <Form.Item name="note" style={{ flex: 1, marginBottom: 0 }}>
                <Input.TextArea autoSize variant="borderless" placeholder="Дополнительная информация" />
              </Form.Item>
            </Descriptions.Item>
          </Descriptions>

          <Divider>Получатель</Divider>

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Имя">
              <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                <Form.Item
                  name="recipient_fullname"
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: 'Введите имя получателя' }]}
                >
                  <Input placeholder="Имя получателя" variant="borderless" />
                </Form.Item>
                <Button icon={<CopyOutlined />} onClick={() => copyFieldValue("recipient_fullname")} />
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Телефон">
              <Space style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                <Form.Item
                  name="recipient_phone"
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: 'Введите телефон получателя' }]}
                >
                  <Input placeholder="+7 (999) 999-99-99" variant="borderless" />
                </Form.Item>
                <Button icon={<CopyOutlined />} onClick={() => copyFieldValue("recipient_phone")} />
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Form>

        {!isNew && docId && (
          <>
            <Divider>Ссылки</Divider>

            {links && isGetLinks ? (
              Object.keys(links).map((key) => {
                const link = links[key];
                const absoluteUrl = ensureAbsoluteUrl(link.url);

                const handleCopy = () => {
                  navigator.clipboard
                    .writeText(absoluteUrl)
                    .then(() => {
                      message.success("Ссылка скопирована в буфер обмена");
                    })
                    .catch((err) => {
                      message.error("Не удалось скопировать ссылку");
                      console.error("Ошибка копирования:", err);
                    });
                };

                const roleTitle = linksKeys.find((item) => item.key === link.role)?.title || link.role;

                return (
                  <Card key={link.id || key} style={{ marginBottom: 12 }}>
                    <Space style={{ marginBottom: 8, width: "100%", display: "flex", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong>{roleTitle}</strong>
                        {link.created_at && (
                          <div>
                            <small>Создано: {link.created_at}</small>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 12 }}>
                        <Button
                          href={absoluteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          icon={<LoginOutlined />}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Перейти
                        </Button>
                        <Button
                          icon={<CopyOutlined />}
                          onClick={handleCopy}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Копировать
                        </Button>
                      </div>
                    </Space>
                  </Card>
                );
              })
            ) : linksLoading ? (
              <Space style={{ width: "100%", justifyContent: "center" }}>
                <Spin />
              </Space>
            ) : (
              <Button style={{ width: "100%" }} onClick={getDocSalesLinks}>
                Получить ссылки
              </Button>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default DocSalesOrderDrawer;